import MainChatContainer from "../../components/chat/MainChatContainer";
import { ExampleChatService } from "@chatscope/use-chat/dist/examples/ExampleChatService";
import {
  BasicStorage,
  ChatProvider,
  AutoDraft,
  IStorage,
  UpdateState,
  ChatMessage,
  MessageContentType,
} from "@chatscope/use-chat";
import { nanoid } from "nanoid";
import { useTwilioChat } from "../../hooks/useTwilioChat";
import { AuthContextInterface, useAuth } from "../../context/AuthContext";
import UserModel from "../../api/models/UserModel";
import { useCallback, useEffect, useMemo } from "react";
import ConversationModel from "../../api/models/ConversationModel";
import { Conversation, Message } from "@twilio/conversations";

export default function ActiveChatPage() {
  const {
    conversationsList,
    getUnreadMessagesCount,
    getConversationsData,
    twilioClient,
  } = useTwilioChat();

  const messageIdGenerator = (message: ChatMessage<MessageContentType>) =>
    nanoid();
  const groupIdGenerator = () => nanoid();
  const auth: AuthContextInterface = useAuth()!;

  const userChatStorage = useMemo(() => {
    return new BasicStorage<ConversationModel>({
      groupIdGenerator,
      messageIdGenerator,
    });
  }, [conversationsList]);

  const { updateConversation } = userChatStorage;
  const userChatModal = new UserModel(auth?.currentUser);

  const chats = { name: userChatModal?.fullName, storage: userChatStorage };

  useEffect(() => {
    if (!!twilioClient) {
      twilioClient!.addListener("messageAdded", onMessageAdded);
      setTimeout(() => {
        twilioClient!.addListener("conversationAdded", onConversationAdded);
      }, 2000);
    }

    return () => {
      if (!!twilioClient) twilioClient!.removeAllListeners();
    };
  }, [twilioClient]);

  const onMessageAdded = (message: Message) => {
    // refetchConversations();
  };

  const onConversationAdded = (conversation: Conversation) => {
    // setTimeout(() => {
    //   refetchConversations(conversation.sid);
    // }, 500);
  };

  const refetchConversations = async (activeConversationId?: string) => {
    await getConversationsData(twilioClient!);
    createUserConversationsStates(
      chats,
      conversationsList,
      activeConversationId
    );
  };

  const serviceFactory = (
    storage: IStorage<any, any>,
    updateState: UpdateState
  ) => {
    return new ExampleChatService(storage, updateState);
  };

  const createUserConversationsStates = useCallback(
    (
      chats: {
        name: string | undefined;
        storage: BasicStorage<ConversationModel>;
      },
      conversations?: ConversationModel[],
      activeConversationId?: string
    ) => {
      const sortedConversations = sortConversations(
        conversations || conversationsList
      );
      sortedConversations.forEach((conversation) => {
        conversation?.participants.forEach((participant: any) => {
          const userData = new UserModel(participant?.user);
          chats.storage.addUser(userData.chatUser);
          if (conversation.isValid(auth.currentUser!.twilioIdentity!)) {
            chats.storage.addConversation(conversation.createConversation());
          } else {
            chats.storage.removeConversation(conversation.twilioId);
          }
        });
      });
      if (!!activeConversationId) {
        chats.storage.setActiveConversation(activeConversationId);
      }
    },
    [chats, conversationsList, getUnreadMessagesCount]
  );

  function sortConversations(list: ConversationModel[]): ConversationModel[] {
    return list.sort((a, b) => b.unreadMessagesCount - a.unreadMessagesCount);
  }

  createUserConversationsStates(chats);

  return (
    <ChatProvider
      serviceFactory={serviceFactory}
      storage={userChatStorage}
      config={{
        typingThrottleTime: 250,
        typingDebounceTime: 900,
        debounceTyping: true,
        autoDraft: AutoDraft.Save | AutoDraft.Restore,
      }}
    >
      <MainChatContainer
        updateConversation={updateConversation}
        user={userChatModal}
        refetchConversations={refetchConversations}
      />
    </ChatProvider>
  );
}
